<template>
	<AppLayout
		:title="rotation.college.name"
		:subtitle="election && canVote ? transChoice('admin.users.rotations.limit_count', election.limit) : ''"
		:return-url="route('association.representative-rotations.history.index')"
		:return-label="trans('admin.users.rotations.history')"
	>
		<template #actions>
			<PrimaryButton v-if="canVote && election" :color="Color.Green" @click.stop="vote">
				<i class="fa-solid fa-vote-yea mr-2"/>
				{{ trans('roles.actions.vote') }}
			</PrimaryButton>
		</template>
		<Card v-if="canVote && election">
			<List>
				<ListItem v-for="electionCandidate in election.candidates" :key="`election-${election.id}-candidate-${electionCandidate.id}`" :title="electionCandidate.candidate.title" @click.stop="toggleVote(electionCandidate)">
					<i v-if="candidateIsSelected(electionCandidate)" class="fa-solid fa-circle-check text-lg text-green-600 dark:text-green-400"/>
					<i v-else class="fa-regular fa-circle text-lg"/>
				</ListItem>
			</List>
		</Card>
		<Alert v-else :color="Color.Blue">{{ trans('assemblies.elections.wait') }}</Alert>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {Color} from '@/Types/Enums/Color'
import {useEchoModel} from '@laravel/echo-vue'
import Card from '@/Components/Cards/Card.vue'
import List from '@/Components/Lists/List.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Alert from '@/Components/Alerts/Alert.vue'
import {trans, transChoice} from 'laravel-vue-i18n'
import ListItem from '@/Components/Lists/ListItem.vue'
import {defineProps, onMounted, ref, type Ref} from 'vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/helpers'
import {type RotationElection} from '@/Types/Elections/RotationElection'
import {type ElectionCandidate} from '@/Types/Elections/ElectionCandidate'

const props = defineProps<{
	canVote:boolean,
	rotation:Rotation,
	selected:Array<number>,
	election:RotationElection|null,
}>()

const working:Ref<boolean> = ref(false)
const canVote:Ref<boolean> = ref(props.canVote)
const rotation:Ref<Rotation> = ref(props.rotation)
const election:Ref<RotationElection|null> = ref(props.election)
const selected:Ref<Array<number>> = ref(props.selected ?? [])

function toggleVote(candidate:ElectionCandidate<any>):void{
	if(candidateIsSelected(candidate)){
		const index = selected.value.findIndex((s:number) => s === candidate.id)
		if(index > -1){
			selected.value.splice(index, 1)
		}
	} else {
		if(election.value?.limit === 1 && selected.value.length === 1){
			selected.value.splice(0, 1)
		}
		if(selected.value.length < election.value?.limit){
			selected.value.push(candidate.id)
		}
	}
}

function candidateIsSelected(candidate:ElectionCandidate<any>):boolean{
	return selected.value.includes(candidate.id)
}

function vote():void{
	if(!working.value && election.value && confirm(trans('assemblies.elections.confirm_vote'))){
		working.value = true
		showInfo(trans('assemblies.elections.voting'))
		axios.post(route('association.representative-rotations.elections.update', rotation.value.id), {
			selected: selected.value,
		}).then(() => {
			showSuccess(trans('assemblies.elections.voted'))
			election.value = undefined
			canVote.value = false
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

onMounted(():void => {
	const rotationEchoModel = useEchoModel('rotations', rotation.value.id)
	rotationEchoModel.channel().listen('RotationElectionStarted', (event:{election:RotationElection}) => {
		election.value = event.election
		canVote.value = true
	})
	rotationEchoModel.channel().listen('RotationElectionEnded', () => {
		election.value = undefined
		canVote.value = false
	})
})
</script>